/**
 * Frequent flyer programs — single source of truth.
 *
 * `value` keys are persisted in passengers.frequent_flyer_program, in the
 * abandoned_trips/bookings passenger JSONB blobs, and in
 * user_loyalty_accounts.program_key (migration 093) — never rename them.
 *
 * `iata` lists every marketing carrier whose flights credit natively to the
 * program (Flying Blue spans Air France + KLM). The FIRST code is the
 * program's display carrier — its tail logo represents the program in UI.
 *
 * `popular` drives the ordering of the add-airline picker on My Profile:
 * US majors first, then the international carriers customers book most.
 */

export const AIRLINE_PROGRAMS = [
  // US carriers
  { value: 'american_aadvantage', label: 'American AAdvantage', airline: 'American Airlines', iata: ['AA'], popular: 1 },
  { value: 'delta_skymiles', label: 'Delta SkyMiles', airline: 'Delta Air Lines', iata: ['DL'], popular: 2 },
  { value: 'united_mileageplus', label: 'United MileagePlus', airline: 'United Airlines', iata: ['UA'], popular: 3 },
  { value: 'southwest_rapid_rewards', label: 'Southwest Rapid Rewards', airline: 'Southwest Airlines', iata: ['WN'], popular: 4 },
  { value: 'alaska_mileage_plan', label: 'Alaska Mileage Plan', airline: 'Alaska Airlines', iata: ['AS'], popular: 5 },
  { value: 'jetblue_trueblue', label: 'JetBlue TrueBlue', airline: 'JetBlue', iata: ['B6'], popular: 6 },
  { value: 'hawaiian_miles', label: 'HawaiianMiles', airline: 'Hawaiian Airlines', iata: ['HA'], popular: 7 },
  { value: 'spirit_free_spirit', label: 'Free Spirit', airline: 'Spirit Airlines', iata: ['NK'], popular: 8 },
  { value: 'frontier_miles', label: 'FRONTIER Miles', airline: 'Frontier Airlines', iata: ['F9'], popular: 9 },
  // International
  { value: 'air_canada_aeroplan', label: 'Air Canada Aeroplan', airline: 'Air Canada', iata: ['AC'], popular: 10 },
  { value: 'british_airways_executive', label: 'British Airways Executive Club', airline: 'British Airways', iata: ['BA'], popular: 11 },
  { value: 'air_france_klm_flying_blue', label: 'Air France–KLM Flying Blue', airline: 'Air France / KLM', iata: ['AF', 'KL'], popular: 12 },
  { value: 'lufthansa_miles_more', label: 'Lufthansa Miles & More', airline: 'Lufthansa', iata: ['LH'], popular: 13 },
  { value: 'emirates_skywards', label: 'Emirates Skywards', airline: 'Emirates', iata: ['EK'], popular: 14 },
  { value: 'qatar_privilege_club', label: 'Qatar Privilege Club', airline: 'Qatar Airways', iata: ['QR'], popular: 15 },
  { value: 'singapore_krisflyer', label: 'Singapore KrisFlyer', airline: 'Singapore Airlines', iata: ['SQ'], popular: 16 },
  { value: 'turkish_miles_smiles', label: 'Turkish Airlines Miles&Smiles', airline: 'Turkish Airlines', iata: ['TK'], popular: 17 },
  { value: 'etihad_guest', label: 'Etihad Guest', airline: 'Etihad Airways', iata: ['EY'] },
  { value: 'cathay_asia_miles', label: 'Cathay Pacific Asia Miles', airline: 'Cathay Pacific', iata: ['CX'] },
  { value: 'qantas_frequent_flyer', label: 'Qantas Frequent Flyer', airline: 'Qantas', iata: ['QF'] },
  { value: 'virgin_atlantic_flying_club', label: 'Virgin Atlantic Flying Club', airline: 'Virgin Atlantic', iata: ['VS'] },
  { value: 'aer_lingus_avios', label: 'Aer Lingus AerClub', airline: 'Aer Lingus', iata: ['EI'] },
  { value: 'iberia_plus', label: 'Iberia Plus', airline: 'Iberia', iata: ['IB'] },
  { value: 'avianca_lifemiles', label: 'Avianca LifeMiles', airline: 'Avianca', iata: ['AV'] },
  { value: 'aeromexico_rewards', label: 'Aeromexico Rewards', airline: 'Aeroméxico', iata: ['AM'] },
  { value: 'copa_connectmiles', label: 'Copa ConnectMiles', airline: 'Copa Airlines', iata: ['CM'] },
  { value: 'ana_mileage_club', label: 'ANA Mileage Club', airline: 'ANA', iata: ['NH'] },
  { value: 'jal_mileage_bank', label: 'JAL Mileage Bank', airline: 'Japan Airlines', iata: ['JL'] },
  { value: 'korean_air_skypass', label: 'Korean Air SKYPASS', airline: 'Korean Air', iata: ['KE'] },
  { value: 'air_china_phoenix_miles', label: 'Air China PhoenixMiles', airline: 'Air China', iata: ['CA'] },
  { value: 'other', label: 'Other' },
];

const PROGRAM_BY_VALUE = new Map(AIRLINE_PROGRAMS.map((p) => [p.value, p]));

export function getProgramByValue(value) {
  return PROGRAM_BY_VALUE.get(value) || null;
}

/** Display label for a stored program key; falls back to the raw value. */
export function getProgramLabel(value) {
  if (!value) return '';
  return PROGRAM_BY_VALUE.get(value)?.label || value;
}

/** Program whose carriers include the given marketing-carrier IATA code. */
export function getProgramForIata(iata) {
  const code = (iata || '').toString().trim().toUpperCase();
  if (!code) return null;
  return AIRLINE_PROGRAMS.find((p) => p.iata?.includes(code)) || null;
}

/**
 * Airlines shown in the My Profile add-airline picker: every program with
 * a carrier, `popular` ones first in rank order.
 */
export const PICKER_AIRLINES = AIRLINE_PROGRAMS
  .filter((p) => Array.isArray(p.iata) && p.iata.length > 0)
  .sort((a, b) => (a.popular || 99) - (b.popular || 99) ||
    a.airline.localeCompare(b.airline));

/**
 * Same-origin airline tail logo (server/airline-logo.js proxies Kiwi's
 * 64×64 PNG set with a Duffel SVG fallback) — safe on mobile/PWA where
 * cross-origin images can fail silently.
 */
export function airlineTailLogoUrl(iata) {
  const code = (iata || '').toString().trim().toUpperCase();
  return code ? `/api/airline-logo/${code}.png` : '';
}
